import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import { gettext } from "web/script/modules/django-i18n";
import React, { RefObject, useEffect, useState } from "react";
import withReduxProvider from "web/react/redux-provider";
import { languageIsEnglish } from "web/script/modules/language";
import FollowDesignerButton from "web/react/components/follow-designer-button/follow-designer-button";
import Button from "web/react/components/button/button";
import FollowStickyBanner from "web/react/components/follow-sticky-banner/follow-sticky-banner";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import environment from "web/script/modules/environment";
import ProfileHeaderBaustein from "web/react/components/profile-header/profile-header-baustein";
import { ReduxStoreState } from "web/redux/store";
import { useSelector as useReduxSelector } from "react-redux";
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./profile-header.css";

interface ProfileHeaderWithLogoProps {
    title: string;
    profile: {
        follow_button?: {
            lyst_id: number;
            registration_reason?: string;
            followed_text: string;
            notfollowed_text: string;
            notfollowed_text_top: string;
            is_authenticated: string;
            is_following: string;
            description: string;
            ios_sticky_description: string;
        };
        small_photo_url: string;
        name?: string;
        title?: string;
        retailer_hub_link?: string;
        features?: {
            pmt_brand_ads?: boolean;
        };
        brand_ad?: {
            banner_image: string;
            brand_logo: string;
            brand_name: string;
            link: string;
            mpu_image: string;
        };
    };
    followCTARef?: RefObject<HTMLDivElement>;
    isIos: boolean;
    isTopCTAinView: boolean;
    isShopHeader?: boolean;
}

function extractTitleState(state: ReduxStoreState): string {
    let titleState = state.feedFiltersReducer.title;
    if (titleState !== null) {
        return titleState;
    } else {
        return "";
    }
}

export function ProfileHeaderWithLogo({
    title,
    profile,
    followCTARef,
    isIos,
    isTopCTAinView,
    isShopHeader,
}: ProfileHeaderWithLogoProps): JSX.Element {
    const [feedTitle, setFeedTitle] = useState(title);
    const feedTitleState = useReduxSelector(extractTitleState);
    useEffect(() => {
        if (feedTitleState) {
            setFeedTitle(feedTitleState);
        }
    }, [feedTitleState]);

    const pageSubType = environment.get("pageSubType");
    const enableIosToApp = environment.getFeature("mg_du_banner_ios_to_app");
    const isOnDesignerFeed = pageSubType && pageSubType.startsWith("designer");
    const sendIOSUsersToApp = isIos && enableIosToApp && isOnDesignerFeed;
    const followButtonProps = profile.follow_button
        ? {
              lystId: profile.follow_button.lyst_id,
              isAuthenticated: profile.follow_button.is_authenticated,
              isFollowing: profile.follow_button.is_following,
              description: sendIOSUsersToApp
                  ? profile.follow_button.ios_sticky_description
                  : profile.follow_button.description,
              registrationReason: "",
              notFollowedText:
                  enableIosToApp && isOnDesignerFeed
                      ? // 'Get Designer Updates'
                        gettext("designer_updates.get_designer_updates_btn_text")
                      : profile.follow_button.notfollowed_text,
          }
        : null;
    const [showBrandAd, setShowBrandAd] = useState<boolean>(false);
    useEffect(() => {
        setShowBrandAd(
            !!(profile.features && profile.features.pmt_brand_ads && !analytics.isBranchIoEnabled())
        );
    }, [profile.features]);
    return (
        <>
            <div
                className={classnames(styles.wrapper, styles.smallDesign, {
                    [styles.pmt477SmallDesign]: showBrandAd,
                })}
            >
                <div className={styles.brand}>
                    {profile.small_photo_url && (
                        <div className={styles.icon}>
                            <img
                                src={profile.small_photo_url}
                                alt={
                                    // "{designer_name} logotype"
                                    gettext("feed.designer_logo.image_alt_text", {
                                        designer_name: title,
                                    })
                                }
                            />
                        </div>
                    )}
                    {feedTitle && (
                        <h1
                            className={styles.name}
                            dangerouslySetInnerHTML={{
                                __html: feedTitle,
                            }}
                        />
                    )}
                </div>
                <div className={styles.buttonWrapper}>
                    {languageIsEnglish && profile.follow_button && followButtonProps && (
                        <div ref={followCTARef} className={styles.followButtonWrapper}>
                            <FollowDesignerButton
                                className={styles.followButton}
                                followedText={profile.follow_button.followed_text}
                                notFollowedText={profile.follow_button.notfollowed_text_top}
                                analyticsParameters={["du_feed_cta", "click_top_cta", "du_cta"]}
                                lystId={followButtonProps.lystId}
                                registrationReason={followButtonProps.registrationReason}
                                isAuthenticated={followButtonProps.isAuthenticated}
                                isFollowing={followButtonProps.isFollowing}
                            />
                        </div>
                    )}
                    {languageIsEnglish && profile.retailer_hub_link && (
                        <Button
                            styleType="secondary"
                            className={styles.promos}
                            href={profile.retailer_hub_link}
                            disabled={false}
                            fullWidth={false}
                            fullHeight={false}
                            openInNewTab={false}
                        >
                            <SVGIcon className={styles.promosIcon} name={"tag"} />
                            {// "Promo’s"
                            gettext("promotion_message.designer.promo_btn_text")}
                        </Button>
                    )}
                </div>
            </div>
            <ProfileHeaderBaustein
                designerLogoOnSmallScreens={true}
                isShopHeader={isShopHeader}
                profile={profile}
            />
            {profile.follow_button && profile.follow_button.is_authenticated != "true" && (
                <FollowStickyBanner
                    profileImage={profile.small_photo_url}
                    followedText={profile.follow_button.followed_text}
                    forceDisplay={!isTopCTAinView}
                    {...followButtonProps}
                />
            )}
        </>
    );
}

export default withReduxProvider(ProfileHeaderWithLogo);
